function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }
function _iterableToArrayLimit(arr, i) { var _i = null == arr ? null : "undefined" != typeof Symbol && arr[Symbol.iterator] || arr["@@iterator"]; if (null != _i) { var _s, _e, _x, _r, _arr = [], _n = !0, _d = !1; try { if (_x = (_i = _i.call(arr)).next, 0 === i) { if (Object(_i) !== _i) return; _n = !1; } else for (; !(_n = (_s = _x.call(_i)).done) && (_arr.push(_s.value), _arr.length !== i); _n = !0); } catch (err) { _d = !0, _e = err; } finally { try { if (!_n && null != _i["return"] && (_r = _i["return"](), Object(_r) !== _r)) return; } finally { if (_d) throw _e; } } return _arr; } }
function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { convertNameToLabel } from '../helpers';
import { useToggle } from '../../utils';

/**
 *
 * A dynamic label associated with an input component.
 *
 * This component is used within {@link LabeledField}, and therefore is incorporated into most `lp-components` input components by default.
 *
 * The text of the label is set using the following rules:
 * - If the `label` prop is set to `false`, the label is hidden completely
 * - Else If the component is passed children, the children will be displayed within the `label`
 * - Else If the `label` prop is set to a string, the label will display that text
 * - Otherwise, the label will be set using the `name` prop.
 *
 * If `name` is used to set the text, it will be stripped of its prefixes and converted to [start case](https://en.wikipedia.org/wiki/Letter_case#Stylistic_or_specialised_usage).
 *
 * For instance: `'person.firstName'` becomes `'First Name'`
 *
 * Note: When using third party form libraries (e.g., [Redux Form](https://redux-form.com)), it's likely that setting the `required` prop will turn on the browser's automatic validation, which could cause the library to behave unexpectedly. If the browser validation behavior is causing issues, then add a `noValidate` prop to the form to [turn off](https://developer.mozilla.org/en-US/docs/Learn/HTML/Forms/Form_validation#Validating_forms_using_JavaScript) automatic validation. (e.g., `<form noValidate></form>`)
 *
 * @name InputLabel
 * @type Function
 * @param {String} name - The name of the associated input
 * @param {String} [id=name] - The id of the associated input (defaults to name)
 * @param {String} [hint] - A usage hint for the associated input
 * @param {String|Boolean} [label] - Custom text for the label
 * @param {String} [tooltip] - A message to display in a tooltip
 * @param {Boolean} [required] - A boolean value to indicate whether the field is required
 * @param {String} [requiredIndicator] - Custom character to denote a field is required (optional, default `''`)

 * @example
 *
 * // A custom input to use with redux-form
 *
 * function EmailInput ({
 *   input: { name, value, onBlur, onChange },
 *   label,
 * }) {
 *   return (
 *      <div>
 *       <InputLabel name={name} label={label} />
 *       <input {...{
 *          type: 'email',
 *          name,
 *          value,
 *          onBlur,
 *          onChange,
 *       }}
 *     </div>
 *   )
 * }
 *
 */

var propTypes = {
  children: PropTypes.node,
  hint: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  name: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
  required: PropTypes.bool,
  requiredIndicator: PropTypes.string,
  className: PropTypes.string
};
var defaultProps = {
  children: null,
  hint: '',
  id: '',
  label: '',
  tooltip: '',
  requiredIndicator: '',
  className: ''
};
function InputLabel(_ref) {
  var children = _ref.children,
    hint = _ref.hint,
    id = _ref.id,
    label = _ref.label,
    name = _ref.name,
    tooltip = _ref.tooltip,
    required = _ref.required,
    requiredIndicator = _ref.requiredIndicator,
    className = _ref.className;
  var _useToggle = useToggle(),
    _useToggle2 = _slicedToArray(_useToggle, 2),
    tooltipShown = _useToggle2[0],
    toggleTooltipShown = _useToggle2[1];
  var labelToDisplay = children || label || convertNameToLabel(name);
  return /*#__PURE__*/React.createElement("span", null, label !== false && /*#__PURE__*/React.createElement("label", {
    htmlFor: id || name,
    className: className
  }, labelToDisplay, required && requiredIndicator && /*#__PURE__*/React.createElement("span", {
    className: "required-indicator",
    "aria-hidden": "true"
  }, requiredIndicator), hint && /*#__PURE__*/React.createElement("i", null, hint)), tooltip && /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "tooltip-trigger",
    onClick: function onClick() {
      return toggleTooltipShown();
    }
  }), tooltip && /*#__PURE__*/React.createElement("div", {
    className: classnames('tooltip-content', {
      'is-active': tooltipShown
    })
  }, tooltip));
}
InputLabel.propTypes = propTypes;
InputLabel.defaultProps = defaultProps;
export default InputLabel;